import React, { FC, PropsWithChildren } from 'react';
import { ToastContainer } from 'react-toastify';

import { ThemeProvider } from '@mui/material/styles';
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit';
import LocalStorage from '@src/LocalStorage';
import theme from '@src/theme/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import AuthorizationContext from '@contexts/common/AuthorizationContext';
import ModalsProvider from '@contexts/common/ModalContext';

import { Wagmi } from '@tools/wagmi';

import Layout from '@components/common/Layout';

import Modals from '@modules/modals';

import {
  TOAST_DEFAULT_AUTO_CLOSE_TIME,
  TOAST_DEFAULT_POSITION,
} from '@constants/common/toast.constants';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time in milliseconds after data is considered stale
      // Prevents unnecessary refetches when data is still fresh
      // Reduces re-renders and improves performance when components mount
      staleTime: 30_000, // 30 seconds

      // Time in milliseconds before inactive queries are garbage collected
      // Keeps cached data available for 5 minutes after component unmounts
      // Helps avoid refetching when user navigates back to the same page
      gcTime: 5 * 60 * 1000, // 5 minutes

      // Disable automatic refetch when window regains focus
      // Prevents unnecessary network requests and re-renders when user switches tabs
      // Improves performance, especially with many active queries (like marketplace orders)
      refetchOnWindowFocus: false,

      // Disable automatic refetch when component mounts if data is still fresh
      // Uses cached data if it's within staleTime, reducing initial load time
      // Prevents cascading re-renders when mounting components with many queries
      refetchOnMount: false,

      // Number of retry attempts for failed queries
      // Reduced from default (3) to minimize network overhead
      // Helps prevent UI freezing when multiple queries fail simultaneously
      retry: 1,
    },
  },
});

const Providers: FC<PropsWithChildren> = ({ children }) => (
  <ModalsProvider>
    <LocalStorage>
      <Wagmi>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider theme={darkTheme()} locale="en-US">
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
            <ThemeProvider theme={theme}>
              <AuthorizationContext>
                <Layout>{children}</Layout>
                <Modals />
                <ToastContainer
                  position={TOAST_DEFAULT_POSITION}
                  autoClose={TOAST_DEFAULT_AUTO_CLOSE_TIME}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  icon={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                />
              </AuthorizationContext>
            </ThemeProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </Wagmi>
    </LocalStorage>
  </ModalsProvider>
);

export default Providers;
